import { useState, useEffect, useRef, useCallback, Fragment, lazy, Suspense } from 'react'
import {
  FiRefreshCw, FiExternalLink, FiCopy, FiCheck, FiPlus, FiX,
  FiUsers, FiUserCheck, FiUserX, FiTrendingUp, FiInfo, FiChevronRight, FiChevronDown,
} from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import LandingEditorPanel from './LandingEditorPanel'
import LeadTimeline from './LeadTimeline'

/**
 * Editor visual novo (Puck), atrás de `lazy` de propósito: são ~500 KB de JS e
 * ~107 KB de CSS que só quem abre esta aba precisa baixar. Estaticamente, eles
 * cairiam no chunk inicial do CRM — que já tem 2,4 MB — para todo mundo.
 */
const LandingPuckEditor = lazy(() => import('./LandingPuckEditor'))

interface Overview {
  total_sessions: number
  completed: number
  abandoned: number
  in_progress: number
  conversion_rate: number | null
}
interface ByStep { step: number; count: number }
interface ByCampaign { campaign_slug: string; total: number; completed: number; conversion_rate: number | null }
// Auditoria da escolha automática de líder (wizard_sessions.leader_choice) —
// gravada no momento do CEP; sessões antigas não têm (null).
interface LeaderChoiceRank { code: string; nome: string | null; distanceKm: number | null; cargaSemana: number; tier: string | null; falhas: number; score: number }
interface LeaderChoice {
  at: string
  cep: string
  geo: { fonte: string | null; confianca: string | null; cidade: string | null } | null
  reason: string
  candidates: number
  tierAPool: number | null
  excludedTierC: number | null
  ranking: LeaderChoiceRank[]
}
interface RecentSession { id: string; campaign_slug: string; nome: string | null; cidade: string | null; motivacao: string | null; completed_at: string; leader_code: string | null; leader_nome: string | null; leader_choice: LeaderChoice | string | null }
interface Campaign { slug: string; name: string; ad_id: string | null; active: number; created_at: string }

// Fluxo reduzido (2026-06-24): Motivação → Cadastro unificado (nome+WhatsApp+CEP+LGPD).
// Steps 3+ são pós-cadastro (sucesso/produtos), não contam no funil de abandono.
const STEP_LABELS: Record<number, string> = {
  0: 'Início', 1: 'Motivação', 2: 'Cadastro', 3: 'Concluído', 4: 'Produtos', 5: 'Pedido'
}
const MOTIVACAO_LABELS: Record<string, string> = {
  renda_extra: 'Renda extra', sair_clt: 'Sair do CLT', horario_flexivel: 'Horário flexível', trabalhar_de_casa: 'Trabalhar de casa'
}

function fmtPct(v: number | null) { return v != null ? `${v}%` : '—' }
function fmtN(v: number | null | undefined) { return v ?? 0 }

const GEO_FONTE_LABELS: Record<string, string> = {
  brasilapi: 'BrasilAPI', google: 'Google Maps',
  nominatim_address: 'Nominatim (nível de rua)', nominatim: 'Nominatim (centróide da cidade)',
}
const CHOICE_REASON_LABELS: Record<string, string> = {
  no_cep: 'CEP não geocodificado — sem coordenada confiável, ninguém foi atribuído automaticamente.',
  low_confidence_geo: 'Geocodificação de baixa confiança (caiu no centróide da cidade) — distância seria falsa, atribuição segurada pra escolha humana.',
  no_proven_in_radius: 'Nenhuma líder elegível no raio (ativa + lead nos últimos 90 dias + telefone válido + sem falhas) — segurado pra escolha humana.',
  no_responsive_in_radius: 'Só sobraram líderes tier C (não respondem follow-up) no raio — segurado pra escolha humana.',
  error: 'Erro na seleção automática.',
}

function parseChoice(raw: LeaderChoice | string | null): LeaderChoice | null {
  if (!raw) return null
  if (typeof raw === 'string') { try { return JSON.parse(raw) } catch { return null } }
  return raw
}

// Card "por que essa líder?" — tradução legível do JSON de auditoria.
function ChoiceExplanation({ c }: { c: LeaderChoice }) {
  const geo = c.geo
  const fonte = geo?.fonte ? (GEO_FONTE_LABELS[geo.fonte] || geo.fonte) : null
  const [win, ...rest] = c.ranking || []
  return (
    <div className="rounded-lg bg-muted/40 border border-border/60 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
      <p>
        <span className="font-semibold text-foreground">Como a líder foi escolhida:</span>{' '}
        CEP {c.cep}{geo?.cidade ? ` (${geo.cidade})` : ''} geocodificado
        {fonte ? ` por ${fonte}` : ''}{geo?.confianca ? ` com confiança ${geo.confianca}` : ''}.
      </p>
      {c.reason === 'ok' ? (
        <>
          <p className="mt-1">
            <b className="text-foreground">{c.candidates}</b> líder{c.candidates === 1 ? '' : 'es'} elegíve{c.candidates === 1 ? 'l' : 'is'} (ativa, no raio, com lead nos últimos 90 dias, telefone válido, sem falhas de entrega, fora do tier C)
            {c.tierAPool != null && c.tierAPool > 0 && <> — decisão restrita ao pool tier A ({c.tierAPool}).</>}
          </p>
          {win && (
            <p className="mt-1">
              <b className="text-emerald-600 dark:text-emerald-400">Escolhida: {win.nome || win.code}</b> — {win.distanceKm != null ? `${win.distanceKm.toLocaleString('pt-BR')} km` : 'distância n/d'}, {win.cargaSemana} lead{win.cargaSemana === 1 ? '' : 's'} na semana{win.tier ? `, tier ${win.tier}` : ''}{win.falhas ? `, ${win.falhas} falha(s) recente(s)` : ', 0 falhas'} (menor score carga × distância).
            </p>
          )}
          {rest.length > 0 && (
            <p className="mt-1">
              Alternativas: {rest.map((r, i) => `${i + 2}º ${r.nome || r.code} (${r.distanceKm != null ? `${r.distanceKm.toLocaleString('pt-BR')} km` : 'n/d'}, ${r.cargaSemana} lead${r.cargaSemana === 1 ? '' : 's'}${r.tier ? `, tier ${r.tier}` : ''})`).join('; ')}.
            </p>
          )}
        </>
      ) : (
        <p className="mt-1">{CHOICE_REASON_LABELS[c.reason] || `Sem atribuição automática (${c.reason}).`}</p>
      )}
    </div>
  )
}

export function WizardCaptacaoPanel({ colapsado, onColapsar }: {
  /** Estado atual da sidebar do CRM, para poder devolvê-lo ao sair. */
  colapsado?: boolean
  /** Pede o recolhimento da sidebar do CRM. */
  onColapsar?: (v: boolean) => void
} = {}) {
  const { authenticatedFetch } = useAuth()
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(false)
  const [overview, setOverview] = useState<Overview | null>(null)
  const [byStep, setByStep] = useState<ByStep[]>([])
  const [byCampaign, setByCampaign] = useState<ByCampaign[]>([])
  const [recent, setRecent] = useState<RecentSession[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  // 'landing-novo' é o editor visual em construção. Convive com o antigo de
  // propósito: os dois gravam em linhas diferentes do banco, e a página pública
  // só troca de renderizador quando alguém aperta "Publicar esta versão" lá.
  const [tab, setTab] = useState<'funil' | 'campanhas' | 'landing' | 'landing-novo'>('funil')

  /**
   * Recolhe a sidebar do CRM enquanto se edita a landing, e devolve o estado
   * anterior ao sair.
   *
   * Guardar o valor de antes importa: quem já trabalhava com a sidebar
   * recolhida não quer que sair da aba a reabra. O ref segura essa memória
   * entre as duas idas ao efeito.
   */
  const colapsoAnterior = useRef<boolean | null>(null)
  useEffect(() => {
    if (!onColapsar) return
    const editando = tab === 'landing' || tab === 'landing-novo'
    if (editando) {
      if (colapsoAnterior.current === null) colapsoAnterior.current = !!colapsado
      onColapsar(true)
    } else if (colapsoAnterior.current !== null) {
      onColapsar(colapsoAnterior.current)
      colapsoAnterior.current = null
    }
    // `colapsado` de fora não entra nas dependências de propósito: ele muda por
    // causa deste próprio efeito, e reagir a isso viraria laço.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, onColapsar])

  // Sair do painel inteiro com a aba de edição aberta também devolve a sidebar.
  useEffect(() => () => {
    if (onColapsar && colapsoAnterior.current !== null) onColapsar(colapsoAnterior.current)
  }, [onColapsar])
  const [newCampaign, setNewCampaign] = useState({ slug: '', name: '', ad_id: '' })
  const [creating, setCreating] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  // Linha expandida com o "por que essa líder?" (auditoria do rodízio)
  const [choiceOpen, setChoiceOpen] = useState<string | null>(null)
  // Nome do lead abre a linha do tempo: landing + acompanhamento da líder.
  const [timelineOpen, setTimelineOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [metricsRes, campRes] = await Promise.all([
        authenticatedFetch(`/api/wizard/metrics?days=${days}`),
        authenticatedFetch('/api/wizard/campaigns'),
      ])
      if (metricsRes.ok) {
        const d = await metricsRes.json()
        setOverview(d.overview)
        setByStep(d.by_step || [])
        setByCampaign(d.by_campaign || [])
        setRecent(d.recent || [])
      }
      if (campRes.ok) setCampaigns(await campRes.json())
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch, days])

  useEffect(() => { load() }, [load])

  const copyLink = (slug: string) => {
    const url = `${window.location.origin}/seja-consultora?campaign=${slug}`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(slug)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  const createCampaign = async () => {
    if (!newCampaign.slug || !newCampaign.name) return
    setCreating(true)
    try {
      const r = await authenticatedFetch('/api/wizard/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCampaign),
      })
      if (r.ok) {
        setNewCampaign({ slug: '', name: '', ad_id: '' })
        setShowNewForm(false)
        load()
      }
    } finally { setCreating(false) }
  }

  const toggleCampaign = async (slug: string, active: boolean) => {
    await authenticatedFetch(`/api/wizard/campaigns/${slug}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active }),
    })
    load()
  }

  const maxStep = byStep.length > 0 ? Math.max(...byStep.map(b => b.count)) : 1

  /** Abas em que a tela inteira é editor e cada pixel de altura conta. */
  const editandoLanding = tab === 'landing' || tab === 'landing-novo'

  return (
    <div className="h-full flex flex-col overflow-hidden neu-forms">
      {/* Header + KPIs — mesmo padrão do Acompanhamento (glass-bar, título + ações, KPIs em cards)
          Escondido nas abas de edição da landing: os números são do funil e não
          ajudam em nada a montar a página, mas comem ~150px de altura numa tela
          onde o canvas já disputa espaço com a sidebar do CRM e as duas colunas
          do editor. Continua montado (só oculto) para não refazer o fetch a
          cada ida e volta entre abas. */}
      <div className={`flex-shrink-0 border-b border-border glass-bar neu-bar px-4 py-3 ${
        editandoLanding ? 'hidden' : ''
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <FiUsers className="w-5 h-5 text-violet-500" />
          <h2 className="text-base font-semibold text-foreground">Captação</h2>
          <span className="text-xs text-muted-foreground hidden sm:inline">· landing "Quero ser Consultora" — funil em tempo real</span>
          <div className="ml-auto flex items-center gap-1.5">
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              className="h-8 text-xs rounded-md border border-border bg-background px-2 text-foreground"
            >
              <option value={7}>Últimos 7 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </select>
            <button onClick={load} disabled={loading} title="Atualizar"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border neu-convex text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {[
            { label: 'Sessões iniciadas', value: fmtN(overview?.total_sessions), icon: FiUsers, cls: 'text-foreground' },
            { label: 'Cadastros completos', value: fmtN(overview?.completed), icon: FiUserCheck, cls: 'text-emerald-500' },
            { label: 'Abandonos', value: fmtN(overview?.abandoned), icon: FiUserX, cls: 'text-red-400' },
            { label: 'Conversão', value: fmtPct(overview?.conversion_rate ?? null), icon: FiTrendingUp, cls: 'text-emerald-500' },
          ].map(k => (
            <div key={k.label} className="rounded-lg border border-border bg-background neu-card px-3 py-2">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <k.icon className={`w-3.5 h-3.5 ${k.cls}`} />
                <span className="truncate">{k.label}</span>
              </div>
              <div className={`text-lg font-bold tabular-nums ${k.cls}`}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-barra: abas + ação contextual */}
      <div className="flex-shrink-0 border-b border-border glass-bar neu-bar px-4 py-2 flex items-center gap-1.5">
        {(['funil', 'campanhas', 'landing', 'landing-novo'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${
              tab === t ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            {t === 'funil' ? 'Funil & abandono'
              : t === 'campanhas' ? 'Campanhas'
              : t === 'landing' ? 'Landing'
              : 'Landing (novo)'}
          </button>
        ))}
        {tab === 'campanhas' && (
          <button
            onClick={() => setShowNewForm(v => !v)}
            className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11px] font-medium bg-foreground text-background neu-relief hover:opacity-90 transition-opacity"
          >
            {showNewForm ? <><FiX size={12} /> Cancelar</> : <><FiPlus size={12} /> Nova campanha</>}
          </button>
        )}
      </div>

      {/* Corpo. As duas abas de Landing têm layout próprio (o editor ocupa a
          área inteira), então ficam fora do wrapper com scroll e padding das
          outras duas. */}
      {tab === 'landing' ? (
        <div className="flex-1 min-h-0">
          <LandingEditorPanel />
        </div>
      ) : tab === 'landing-novo' ? (
        <div className="flex-1 min-h-0">
          <Suspense fallback={
            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
              Carregando o editor visual…
            </div>
          }>
            <LandingPuckEditor />
          </Suspense>
        </div>
      ) : (
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">

      {/* Aba: Funil — abandono e campanhas lado a lado; recentes embaixo em
          largura total (tabela compacta com scroll interno — aguenta volume).
          "Por campanha" só entra com 2+ campanhas — com uma só, repete os KPIs
          do header (pedido 2026-07-14). */}
      {tab === 'funil' && (
        <div className="grid gap-4 items-start lg:grid-cols-2">
          {/* Abandono por etapa */}
          {byStep.length > 0 && (
            <div className={`glass-panel neu-card rounded-2xl p-4 ${byCampaign.length > 1 ? '' : 'lg:col-span-2'}`}>
              <p className="text-xs font-medium text-muted-foreground mb-3">Abandono por etapa</p>
              <div className="flex flex-col gap-2">
                {byStep.sort((a, b) => a.step - b.step).map(s => (
                  <div key={s.step} className="flex items-center gap-2">
                    <span className="text-xs w-24 text-muted-foreground shrink-0">{STEP_LABELS[s.step] || `Etapa ${s.step}`}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden neu-gauge-lg">
                      <div
                        className="h-full rounded-full bg-red-400/70"
                        style={{ width: `${Math.round((s.count / maxStep) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-6 text-right">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Por campanha */}
          {byCampaign.length > 1 && (
            <div className="glass-panel neu-card rounded-2xl p-4">
              <p className="text-xs font-medium text-muted-foreground mb-3">Por campanha</p>
              <div className="max-h-[280px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card neu-thead">
                    <tr className="text-muted-foreground border-b border-border">
                      <th className="text-left pb-2 font-medium">Campanha</th>
                      <th className="text-right pb-2 font-medium">Sessões</th>
                      <th className="text-right pb-2 font-medium">Completos</th>
                      <th className="text-right pb-2 font-medium">Conversão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCampaign.map(c => (
                      <tr key={c.campaign_slug} className="border-b border-border/50 last:border-0">
                        <td className="py-1.5 font-mono">{c.campaign_slug}</td>
                        <td className="py-1.5 text-right tabular-nums">{c.total}</td>
                        <td className="py-1.5 text-right tabular-nums text-emerald-500">{c.completed}</td>
                        <td className="py-1.5 text-right tabular-nums font-medium">{fmtPct(c.conversion_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recentes — largura total, uma linha por cadastro, scroll interno */}
          {recent.length > 0 && (
            <div className="glass-panel neu-card rounded-2xl p-4 lg:col-span-2">
              <p className="text-xs font-medium text-muted-foreground mb-3">
                Últimos cadastros completos <span className="font-normal">({recent.length})</span>
              </p>
              <div className="max-h-[380px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card neu-thead">
                    <tr className="text-muted-foreground border-b border-border">
                      <th className="text-left pb-2 font-medium">Nome</th>
                      <th className="text-left pb-2 font-medium hidden md:table-cell">Cidade</th>
                      <th className="text-left pb-2 font-medium hidden sm:table-cell">Motivação</th>
                      <th className="text-left pb-2 font-medium hidden sm:table-cell">Campanha</th>
                      <th className="text-left pb-2 font-medium" title="Líder designada pelo rodízio automático no momento do CEP. O ícone abre a explicação da escolha.">Líder</th>
                      <th className="text-right pb-2 font-medium">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map(r => {
                      const choice = parseChoice(r.leader_choice)
                      return (
                      <Fragment key={r.id}>
                      <tr className="border-b border-border/40 last:border-0">
                        <td className="py-1.5 font-medium max-w-[180px]">
                          <button
                            onClick={() => setTimelineOpen(timelineOpen === r.id ? null : r.id)}
                            title="Ver a linha do tempo deste lead"
                            className={`flex w-full items-center gap-1 truncate text-left transition-colors ${
                              timelineOpen === r.id ? 'text-violet-500' : 'hover:text-violet-500'
                            }`}
                          >
                            {timelineOpen === r.id
                              ? <FiChevronDown className="w-3 h-3 shrink-0" />
                              : <FiChevronRight className="w-3 h-3 shrink-0" />}
                            <span className="truncate">{r.nome || '—'}</span>
                          </button>
                        </td>
                        <td className="py-1.5 text-muted-foreground max-w-[260px] truncate hidden md:table-cell">{r.cidade || '—'}</td>
                        <td className="py-1.5 text-muted-foreground hidden sm:table-cell">{r.motivacao ? (MOTIVACAO_LABELS[r.motivacao] || r.motivacao) : '—'}</td>
                        <td className="py-1.5 text-muted-foreground font-mono hidden sm:table-cell">{r.campaign_slug}</td>
                        <td className="py-1.5 max-w-[180px]">
                          <span className="inline-flex items-center gap-1">
                            <span className="truncate text-muted-foreground">{r.leader_nome || r.leader_code || '—'}</span>
                            {choice && (
                              <button onClick={() => setChoiceOpen(choiceOpen === r.id ? null : r.id)}
                                title="Por que essa líder? Ver a decisão do rodízio (distância, carga, tier e alternativas)"
                                className={`flex-shrink-0 ${choiceOpen === r.id ? 'text-violet-500' : 'text-muted-foreground hover:text-foreground'} transition-colors`}>
                                <FiInfo className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </span>
                        </td>
                        <td className="py-1.5 text-right text-muted-foreground whitespace-nowrap tabular-nums">{r.completed_at ? new Date(r.completed_at).toLocaleDateString('pt-BR') : ''}</td>
                      </tr>
                      {timelineOpen === r.id && (
                        <tr className="border-b border-border/40">
                          <td colSpan={6} className="py-2"><LeadTimeline sessionId={r.id} /></td>
                        </tr>
                      )}
                      {choice && choiceOpen === r.id && (
                        <tr className="border-b border-border/40">
                          <td colSpan={6} className="py-2"><ChoiceExplanation c={choice} /></td>
                        </tr>
                      )}
                      </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!overview && !loading && (
            <p className="col-span-full text-sm text-muted-foreground text-center py-12">Nenhum dado ainda. Compartilhe o link da campanha para começar a coletar.</p>
          )}
        </div>
      )}

      {/* Aba: Campanhas */}
      {tab === 'campanhas' && (
        <div className="flex flex-col gap-3">
          {showNewForm && (
            <div className="glass-panel neu-card rounded-2xl p-4">
              <p className="text-xs font-medium mb-2">Nova campanha</p>
              <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                <input
                  placeholder="slug (ex: reel-renda-extra)"
                  value={newCampaign.slug}
                  onChange={e => setNewCampaign(p => ({ ...p, slug: e.target.value.replace(/[^a-z0-9_-]/gi, '').toLowerCase() }))}
                  className="text-xs border border-border rounded-lg px-3 py-2 bg-background"
                />
                <input
                  placeholder="Nome exibição"
                  value={newCampaign.name}
                  onChange={e => setNewCampaign(p => ({ ...p, name: e.target.value }))}
                  className="text-xs border border-border rounded-lg px-3 py-2 bg-background"
                />
                <input
                  placeholder="ad_id (opcional)"
                  value={newCampaign.ad_id}
                  onChange={e => setNewCampaign(p => ({ ...p, ad_id: e.target.value }))}
                  className="text-xs border border-border rounded-lg px-3 py-2 bg-background"
                />
                <button
                  onClick={createCampaign}
                  disabled={creating || !newCampaign.slug || !newCampaign.name}
                  className="text-xs px-4 py-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-40"
                >
                  {creating ? 'Criando…' : 'Criar'}
                </button>
              </div>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 items-start">
            {campaigns.map(c => {
              const url = `${window.location.origin}/seja-consultora?campaign=${c.slug}`
              return (
                <div key={c.slug} className="glass-panel neu-card rounded-2xl p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-medium truncate">{c.name}</p>
                      {/* emerald-600 é tom de texto pra fundo CLARO: sobre a base
                          escura + tinta 15% ele cai pra 2,9:1. O -400 devolve a
                          legibilidade (mesmo par que o ArchitectureDiagram usa). */}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.active ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                        {c.active ? 'ativa' : 'inativa'}
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{c.slug}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => copyLink(c.slug)}
                      title="Copiar link"
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                    >
                      {copied === c.slug ? <FiCheck size={13} className="text-emerald-500" /> : <FiCopy size={13} />}
                    </button>
                    <a href={url} target="_blank" rel="noopener noreferrer" title="Abrir" className="p-1.5 rounded-lg hover:bg-muted">
                      <FiExternalLink size={13} />
                    </a>
                    <button
                      onClick={() => toggleCampaign(c.slug, !c.active)}
                      className="text-[10px] px-2 py-1 rounded-lg border border-border hover:bg-muted"
                    >
                      {c.active ? 'Pausar' : 'Ativar'}
                    </button>
                  </div>
                </div>
              )
            })}
            {campaigns.length === 0 && !loading && (
              <p className="col-span-full text-xs text-muted-foreground text-center py-12">Nenhuma campanha. Crie uma pelo botão "Nova campanha" acima.</p>
            )}
          </div>
        </div>
      )}

      </div>
      )}
    </div>
  )
}
